import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, CheckCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { clienteMobileService } from '@/services/clienteMobileService';
import type { ClienteApiNotification } from '@/types/clienteMobile';
import { cn, formatDateTime } from '@/lib/utils';

export function ClienteNotificationMenu() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<ClienteApiNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const c = await clienteMobileService.getUnreadCount();
      setCount(c);
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 60_000);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await clienteMobileService.getNotifications(30);
        if (!cancelled) setItems(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const markRead = async (id: string) => {
    await clienteMobileService.markNotificationRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
    load();
  };

  const markAll = async () => {
    await clienteMobileService.markAllNotificationsRead();
    setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
    setCount(0);
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="relative h-10 w-10 text-black hover:bg-black/10"
        aria-label="Notificações"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell className="h-5 w-5" />
        {count > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-[10px] font-bold text-white flex items-center justify-center px-1">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </Button>

      {open ? (
        <div className="absolute right-0 top-full mt-2 w-[min(100vw-2rem,380px)] rounded-xl border border-border bg-card shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-semibold">Notificações</span>
            {items.some((i) => !i.readAt) ? (
              <button
                type="button"
                className="text-xs text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1"
                onClick={() => markAll()}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar lidas
              </button>
            ) : null}
          </div>
          <div className="max-h-[min(70vh,400px)] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8 px-4">Nenhuma notificação.</p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id} className={cn('px-3 py-2.5', !n.readAt && 'bg-amber-500/5')}>
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-medium text-foreground">{n.title}</p>
                      {!n.readAt ? (
                        <button
                          type="button"
                          className="shrink-0 text-muted-foreground hover:text-foreground"
                          title="Marcar lida"
                          onClick={() => markRead(n.id)}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{formatDateTime(n.createdAt)}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
